import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { resolveCustomerWalletQrPayload, buildGoogleWalletAddShortcutUrl } from '../../../shared/utils/walletPassQrPayload';

/**
 * CustomerQRModal - Popup showing QR code for customer pass (rewards page) and link to open/download
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Object} props.customer - Customer object with wallet_pass_id, customer_name
 * @param {Function} props.onClose - Callback when modal is closed
 */
export function CustomerQRModal({ isOpen, customer, onClose }) {
    if (!isOpen || !customer) {
        return null;
    }

    const originalWalletLink =
        customer.pass_url ||
        customer.passUrl ||
        customer.wallet_pass_url ||
        customer.walletPassUrl ||
        null;

    const fallbackRewardsUrl = `${window.location.origin}/customer/rewards/${encodeURIComponent(customer.wallet_pass_id)}`;
    const { qrPayload, openWalletHref, mode } = resolveCustomerWalletQrPayload(
        customer,
        originalWalletLink,
        fallbackRewardsUrl
    );

    const scanPageUrl = `${window.location.origin}/dashboard/scan?passId=${encodeURIComponent(customer.wallet_pass_id)}`;

    const handleOpenPass = () => {
        window.open(scanPageUrl, '_blank', 'noopener,noreferrer');
    };

    const isGoogleSaveLink =
        typeof openWalletHref === 'string' &&
        openWalletHref.startsWith('https://pay.google.com/gp/v/save/');
    const googleShortLink = isGoogleSaveLink ? buildGoogleWalletAddShortcutUrl(customer.wallet_pass_id) : null;

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-labelledby="qr-modal-title"
        >
            <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-start mb-4">
                    <h3 id="qr-modal-title" className="text-lg font-semibold text-gray-800">
                        Download Pass QR Code
                    </h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                    Scan this QR code to download the pass again for{' '}
                    <strong>{customer.customer_name || customer.customer_email}</strong>.
                </p>
                <div className="flex justify-center p-4 bg-gray-50 rounded-lg mb-4 min-h-[120px]">
                    {mode === 'data-url' && qrPayload ? (
                        <img
                            src={qrPayload}
                            alt="Pass QR Code"
                            className="bg-white border rounded p-2 w-[220px] h-[220px] object-contain"
                        />
                    ) : mode === 'too-long' || !qrPayload ? (
                        <p className="text-sm text-amber-900 text-center px-2 self-center">
                            This add-to-wallet link is too long to show as a QR code here. Resend the pass by email or
                            SMS from Generate, or share the link another way.
                        </p>
                    ) : (
                        <div className="bg-white border rounded p-2">
                            <QRCodeSVG value={qrPayload} size={200} level="L" boostLevel={false} marginSize={4} />
                        </div>
                    )}
                </div>
                {isGoogleSaveLink && qrPayload && mode === 'svg' && (
                    <p className="text-xs text-gray-500 mb-3">
                        This QR opens Google Wallet directly to add the pass.
                    </p>
                )}
                {!originalWalletLink && (
                    <p className="text-xs text-gray-500 mb-2">
                        Wallet link wasn’t available for this customer, so this QR opens the rewards portal instead.
                    </p>
                )}
                <div className="flex flex-col gap-2">
                    <button
                        type="button"
                        onClick={() => {
                            if (googleShortLink) {
                                window.open(googleShortLink, '_blank', 'noopener,noreferrer');
                                return;
                            }
                            handleOpenPass();
                        }}
                        className="w-full py-2 px-4 rounded bg-primary text-white font-medium hover:opacity-90"
                    >
                        {googleShortLink ? 'Open Pass Short Link' : 'View Pass in a New Tab'}
                    </button>
                </div>
            </div>
        </div>
    );
}
